import React from 'react';
import type {
  RecapEntry,
  RecapOutcome,
  ResumeOffer,
} from '../../hooks/useLessonApi';
import type { RunMode } from '../../turncard/viewModel';
import { INTERFACE_TEXT } from '../../constants/interfaceText';
import { fill } from '../../today/todayViewModel';
import { unlockPromptPlayback } from '../../speech';
import styles from './ResumeCard.module.css';

interface ResumeCardProps {
  mode: RunMode;
  offer: ResumeOffer;
  onContinue: () => void;
  onRestart: () => void;
}

const OUTCOME_LABEL: Record<RecapOutcome, string> = {
  success: INTERFACE_TEXT.rsOutcomeSuccess,
  skipped: INTERFACE_TEXT.rsOutcomeSkipped,
  tried_again: INTERFACE_TEXT.rsOutcomeTriedAgain,
};

interface RecapRow {
  key: string;
  text: string;
  outcome?: RecapOutcome;
}

/** Lesson recap: the tutor's lines and the phrases the learner covered, with how each went. */
const lessonRows = (recap: RecapEntry[]): RecapRow[] =>
  recap
    .filter((e) => e.text)
    .map((e) => ({
      key: String(e.seq),
      text: e.text as string,
      outcome: e.kind === 'learner' ? e.outcome : undefined,
    }));

/**
 * Check-in recap (BR-043): progress only. One row per part the learner has answered, with its latest
 * outcome and no scene text, meanings or target-language text.
 */
const checkinRows = (recap: RecapEntry[]): RecapRow[] => {
  const latest = new Map<string, RecapOutcome | undefined>();
  recap
    .filter((e) => e.kind === 'learner')
    .forEach((e, i) => {
      latest.set(e.part_id ?? `entry-${i}`, e.outcome);
    });
  return [...latest.entries()].map(([id, outcome], i) => ({
    key: id,
    text: fill(INTERFACE_TEXT.rsPartN, { n: i + 1 }),
    outcome,
  }));
};

/** Offered when the learner opens a lesson or check-in they left unfinished. */
export const ResumeCard: React.FC<ResumeCardProps> = ({
  mode,
  offer,
  onContinue,
  onRestart,
}) => {
  const checkin = mode === 'checkin';
  const rows = checkin ? checkinRows(offer.recap) : lessonRows(offer.recap);
  const handleContinue = (): void => {
    // The tap lets later check-in lines autoplay.
    if (checkin) unlockPromptPlayback();
    onContinue();
  };
  return (
    <div className={styles.resume} data-testid="resume-card">
      <h2 className={styles.heading}>{INTERFACE_TEXT.rsHeading}</h2>
      <p className={styles.step} data-testid="resume-step">
        {fill(INTERFACE_TEXT.rsStep, {
          n: Math.min(offer.step_index + 1, offer.total_steps),
          total: offer.total_steps,
        })}
      </p>
      {rows.length > 0 && (
        <section aria-labelledby="recap-heading">
          <h3 id="recap-heading" className={styles.recapHeading}>
            {INTERFACE_TEXT.rsRecapHeading}
          </h3>
          <ul className={styles.recap} data-testid="resume-recap">
            {rows.map((r) => (
              <li key={r.key}>
                <span>{r.text}</span>
                {r.outcome && (
                  <span className={styles.outcome}>
                    {OUTCOME_LABEL[r.outcome]}
                  </span>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}
      <button
        type="button"
        className={styles.cta}
        onClick={handleContinue}
        data-testid="resume-continue"
      >
        {INTERFACE_TEXT.rsContinue}
      </button>
      <button
        type="button"
        className={styles.alt}
        onClick={onRestart}
        data-testid="resume-restart"
      >
        {INTERFACE_TEXT.rsRestart}
      </button>
      <p className={styles.hint}>{INTERFACE_TEXT.rsRestartHint}</p>
    </div>
  );
};

export default ResumeCard;
